import { Box } from '@mui/material';
import SlotList from '../slot-list';
import EditDetailsPanel from '../edit-details-panel';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from '../flow-detail.types';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { FlowBasicsEventSlotTiming } from '@/components/events/detail/tabs/flows/grid/flow-basics-event-slot-timing';
import { useFlowMode } from '../use-flow-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import {
  flowSlotListColumnScrollbarSx,
  slotListColumnHeaderInsetSx,
  slotListColumnOuterSx,
  slotListScrollAreaSx,
} from './flow-slot-list-column-sx';

/**
 * Pass to `SlotList` as `hideFlowStartSlotRowWhenSmallScreen` only from schedule-tab entry points
 * (wide `FlowScheduleTab` and narrow schedule column in `flow/detail/index.tsx`), not slot assets / flow control.
 */
export const SCHEDULE_SLOT_LIST_HIDE_FLOW_START_ON_SMALL_SCREEN = true;

/** Schedule tab: end-of-flow row has no slot media; omit the VideoLibrary indicator in the right column. */
export const SCHEDULE_SLOT_LIST_HIDE_END_ITEM_MEDIA_INDICATOR = true;

/** Schedule tab: dedicated drag handle in the left column (media-list style); right column narrows by the same width. */
export const SCHEDULE_SLOT_LIST_SHOW_DRAG_HANDLE = true;

/** Schedule tab: borderless info alert without expand chevron to save vertical space. */
export const SCHEDULE_SLOT_LIST_COMPACT_INFO_ALERT = true;

/** Wide layout: slot list + details column. Narrow schedule uses `SlotList` in flow detail `index.tsx` with inline details under rows. */
interface FlowScheduleTabProps {
  setShowFlowDetails: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  selectedSlot?: FlowSlotDto;
  slotTitle: string;
  onSlotDeleted: () => void;
  onSessionPublishSuccess?: () => void;
  eventSlotIdParam?: string | null;
}

/** Details column — padded scroll region. */
const detailsColumnSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  height: '100%',
  px: 1,
  pt: 0,
  ...flowSlotListColumnScrollbarSx,
};

export default function FlowScheduleTab({
  setShowFlowDetails,
  setFlowDetailsDirection,
  onSlotSelect,
  onShowTimingAlert,
  onShowPublishAlert,
  selectedSlot,
  slotTitle,
  onSlotDeleted,
  onSessionPublishSuccess,
  eventSlotIdParam,
}: FlowScheduleTabProps) {
  const flowMode = useFlowMode();
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const event = useEventStore((s) => s.event);
  const isSingleFlowEvent = event && !event.multiFlow;
  const flowForTiming = flowPlan ?? flow;

  return (
    <Box sx={{ display: 'flex', gap: 1, flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}>
      <Box sx={slotListColumnOuterSx}>
        {flowForTiming && eventSlotIdParam && !isSingleFlowEvent ? (
          <Box sx={slotListColumnHeaderInsetSx}>
            <FlowBasicsEventSlotTiming flow={flowForTiming} mode={flowMode} disabled={isOrganizeMode(flowMode)} />
          </Box>
        ) : null}
        <Box sx={slotListScrollAreaSx}>
          <SlotList
            setShowFlowDetails={setShowFlowDetails}
            setFlowDetailsDirection={setFlowDetailsDirection}
            onSlotSelect={onSlotSelect}
            onShowTimingAlert={onShowTimingAlert}
            onShowPublishAlert={onShowPublishAlert}
            onWideScreenSlotDeletedFromList={onSlotDeleted}
            onSessionPublishSuccess={onSessionPublishSuccess}
            hideFlowStartSlotRow
            hideFlowStartSlotRowWhenSmallScreen={SCHEDULE_SLOT_LIST_HIDE_FLOW_START_ON_SMALL_SCREEN}
            hideEndItemMediaIndicator={SCHEDULE_SLOT_LIST_HIDE_END_ITEM_MEDIA_INDICATOR}
            showSlotDragHandle={SCHEDULE_SLOT_LIST_SHOW_DRAG_HANDLE}
            compactInfoAlert={SCHEDULE_SLOT_LIST_COMPACT_INFO_ALERT}
          />
        </Box>
      </Box>
      <Box sx={detailsColumnSx}>
        <EditDetailsPanel
          slot={selectedSlot}
          slotTitle={slotTitle}
          onSlotDeleted={onSlotDeleted}
          onSessionPublishSuccess={onSessionPublishSuccess}
          eventSlotIdParam={eventSlotIdParam}
        />
      </Box>
    </Box>
  );
}
